import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { UserRound, ChevronRight, ChevronLeft, Edit2, Trash2, Plus, X, Trophy, Star, LayoutTemplate, Upload, Globe, Lock, Link2, Bookmark, Flag, Award, Sword, Shield, Zap, Crown, Sparkles, Target, StickyNote, Image as ImageIcon } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_IMAGES = 10;
const MAGENTA = "#e11d74";
const STRIP_PAGE_SIZE = 10;

// Note-link display: 12 fallback icons the user can pick when the target
// note has no image, or when they simply prefer an icon over the note title.
// Keys are stored on the noteLink; the map resolves them at render time.
export const NOTELINK_ICONS = {
  link: Link2,
  bookmark: Bookmark,
  flag: Flag,
  award: Award,
  star: Star,
  sword: Sword,
  shield: Shield,
  zap: Zap,
  crown: Crown,
  sparkles: Sparkles,
  target: Target,
  trophy: Trophy,
};
const NOTELINK_ICON_ORDER = ["link", "bookmark", "flag", "award", "star", "sword", "shield", "zap", "crown", "sparkles", "target", "trophy"];
export const NOTELINK_MAX = 20;
export const NOTELINK_MAX_FEATURED = 4;

// Small helper — format a unix/ISO date for the hover tooltip.
function formatUnlockDate(v) {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return null; }
}

// Static strip of achievements this character has personally unlocked.
// Half-size thumbnails, 10 per row, paginates when more, hover reveals name +
// unlock time. Count is shown on the right of the header. Renders nothing
// when the character has no per-character unlocks.
function CharacterAchievementsStrip({ characterId, achievements = [], pageSize = STRIP_PAGE_SIZE }) {
  const unlocked = useMemo(() => {
    const rows = [];
    for (const ach of achievements) {
      const list = ach?.perCharacterUnlocks || [];
      const hit = list.find((u) => u && u.characterNoteId === characterId);
      if (hit) rows.push({ ach, unlockedAt: hit.unlockedAt || null });
    }
    // Most recent unlocks first, no-date rows sink to the bottom.
    rows.sort((a, b) => {
      const at = a.unlockedAt ? Date.parse(a.unlockedAt) : -Infinity;
      const bt = b.unlockedAt ? Date.parse(b.unlockedAt) : -Infinity;
      return bt - at;
    });
    return rows;
  }, [characterId, achievements]);

  const [page, setPage] = useState(0);
  const total = unlocked.length;
  if (total === 0) return null;

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * pageSize;
  const slice = unlocked.slice(start, start + pageSize);

  return (
    <div
      className="mt-2 rounded-md border border-white/5 bg-black/30 px-2 py-1.5"
      data-testid={`character-unlocks-strip-${characterId}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[11px] text-white/60">
          <Trophy className="w-3 h-3" style={{ color: MAGENTA }} />
          <span>Achievements Unlocked</span>
          {pageCount > 1 && (
            <span className="text-white/40">
              · Page {clampedPage + 1} / {pageCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {pageCount > 1 && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
                className="p-0.5 rounded text-white/50 hover:text-pink-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous"
                data-testid={`character-unlocks-prev-${characterId}`}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={clampedPage >= pageCount - 1}
                className="p-0.5 rounded text-white/50 hover:text-pink-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next"
                data-testid={`character-unlocks-next-${characterId}`}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <span
            className="text-[11px] font-semibold text-pink-300 tabular-nums"
            data-testid={`character-unlocks-count-${characterId}`}
          >
            {total}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        {slice.map(({ ach, unlockedAt }) => {
          const dateStr = formatUnlockDate(unlockedAt);
          const title = dateStr
            ? `${ach.name}\nUnlocked: ${dateStr}`
            : `${ach.name}${unlockedAt === null ? "\nNo unlock date recorded" : ""}`;
          const img = ach.imageUrl || (ach.additionalImages && ach.additionalImages[0]);
          return (
            <div
              key={ach.id}
              className="h-6 w-auto rounded overflow-hidden bg-black/60 border border-white/10 hover:border-pink-400/60 transition-colors shrink-0 flex items-center justify-center"
              title={title}
              data-testid={`character-unlocks-item-${characterId}-${ach.id}`}
            >
              {img ? (
                <img
                  src={img}
                  alt={ach.name}
                  loading="lazy"
                  className="h-6 w-auto max-w-[64px] object-contain"
                />
              ) : (
                <div className="w-6 h-6 flex items-center justify-center text-white/30">
                  <Trophy className="w-3 h-3" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Compact card that renders a character in the game's content list.
// Note: character notes intentionally cannot be pinned to the planner —
// they're per-character personal data (unlocks, stats, images) rather than
// planner items, and Community Game Planners never touch them anyway.
// Small "chip" rendering a single character noteLink. Resolves display
// mode to one of: the target note's title, its imageUrl (32×32), the
// user's custom text, or a picked icon. Hovering the chip shows the
// target note's body text in a floating card so long notes stay
// readable without leaving the character card. Clicking scrolls the
// target note into view and expands it.
function NoteLinkPill({ link }) {
  const [hover, setHover] = useState(false);
  // Rect of the pill, captured on hover so the portal-rendered tooltip
  // can position itself directly below (or above, if there's no room)
  // without inheriting the character card's `overflow-hidden` clipping.
  const [rect, setRect] = useState(null);
  const anchorRef = useRef(null);
  const target = link.target;
  const mode = link.displayMode || "title";
  const preview = (target.text || "").trim();
  const iconKey = link.iconKey && NOTELINK_ICONS[link.iconKey] ? link.iconKey : "link";
  const IconCmp = NOTELINK_ICONS[iconKey] || Link2;

  const openTooltip = () => {
    if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    setHover(true);
  };
  const closeTooltip = () => setHover(false);

  const jumpToNote = () => {
    const el = document.querySelector(`[data-testid="note-title-${target.id}"]`)
      || document.getElementById(`note-${target.id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Expand the note by simulating a click on the title button.
      if (typeof el.click === "function") setTimeout(() => el.click(), 300);
    }
  };

  // Build the visual body based on displayMode. "image" falls through to
  // the icon path when the target note has no imageUrl (spec: give the
  // user a small icon library as fallback).
  let body;
  if (mode === "image" && target.imageUrl) {
    body = (
      <img
        src={target.imageUrl}
        alt=""
        className="w-8 h-8 object-cover rounded"
        loading="lazy"
      />
    );
  } else if (mode === "text" && link.customText) {
    body = <span className="px-2 py-0.5 text-xs text-white/90">{link.customText}</span>;
  } else if (mode === "icon" || (mode === "image" && !target.imageUrl)) {
    body = (
      <span className="w-8 h-8 flex items-center justify-center rounded bg-cyan-500/10 border border-cyan-400/20 text-cyan-300">
        <IconCmp className="w-4 h-4" />
      </span>
    );
  } else {
    // title (default)
    body = <span className="px-2 py-0.5 text-xs text-white/90 truncate max-w-[180px]">{target.title || "Untitled note"}</span>;
  }

  // Position the tooltip: prefer below the pill, but flip above if there's
  // not enough room. Clamp horizontally so it stays within the viewport.
  const TOOLTIP_W = 288; // w-72
  const TOOLTIP_MAX_H = 224; // max-h-56
  let tooltipStyle = null;
  if (rect) {
    const belowSpace = window.innerHeight - rect.bottom;
    const above = belowSpace < TOOLTIP_MAX_H + 12 && rect.top > TOOLTIP_MAX_H + 12;
    const top = above ? rect.top - TOOLTIP_MAX_H - 4 : rect.bottom + 4;
    let left = rect.left;
    if (left + TOOLTIP_W > window.innerWidth - 8) left = window.innerWidth - TOOLTIP_W - 8;
    if (left < 8) left = 8;
    tooltipStyle = { position: "fixed", top, left, width: TOOLTIP_W, maxHeight: TOOLTIP_MAX_H, zIndex: 9999 };
  }

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex items-center rounded border border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15 hover:border-cyan-400/40 transition-colors cursor-pointer align-middle"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
      onFocus={openTooltip}
      onBlur={closeTooltip}
      onClick={jumpToNote}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jumpToNote(); } }}
      title={target.title || "Untitled note"}
      data-testid={`notelink-pill-${link.id}`}
    >
      {body}
      {hover && preview && tooltipStyle && createPortal(
        <div
          style={tooltipStyle}
          className="overflow-y-auto rounded border border-white/10 bg-[#0d0d0d] p-2 text-xs text-white/85 whitespace-pre-wrap shadow-xl pointer-events-none"
          data-testid={`notelink-hover-${link.id}`}
        >
          <div className="text-cyan-300/80 font-medium mb-1 truncate">{target.title || "Untitled note"}</div>
          {preview}
        </div>,
        document.body
      )}
    </span>
  );
}

export function CharacterDisplay({ note, onEdit, onDelete, achievements = [], availableNotes = [], headerExtras = null }) {
  // Three-tier expansion cycle:
  //   0 = Minimal — just name + arrow (like a plain-note collapsed row)
  //   1 = Medium  — portrait + achievements strip + featured stats
  //   2 = Full    — everything (all stats, description text, image grid)
  // The user cycles through with the arrow. Start collapsed at Minimal so
  // long character lists stay compact by default; a single click reveals
  // the portrait view they saw before.
  const [tier, setTier] = useState(0);
  const cycleTier = () => setTier((t) => (t + 1) % 3);
  const showMedium = tier >= 1;
  const showFull = tier >= 2;
  const statCount = (note.statistics || []).length;
  // Featured stats preserve their author-defined order (the drag-reorder in
  // the editor). Medium tier keeps just the top 2 so 2/3/4-column grids can
  // breathe; Full tier still shows up to 4. The container query below also
  // collapses to 1-column on narrow cards.
  const allFeaturedStats = (note.statistics || []).filter((s) => s?.featured);
  const featuredStats = allFeaturedStats.slice(0, showFull ? 4 : 2);
  const images = note.images || [];
  const imgCount = images.length;
  const primaryImage = images[0] || null;
  const additionalImages = images.slice(1);

  // Resolve note-links against the pool of plain notes on this game so we
  // can render title / image / icon / custom text and preview the note's
  // body text on hover. Missing targets are silently dropped so a deleted
  // note doesn't crash the card.
  const notesById = useMemo(() => {
    const m = new Map();
    for (const n of availableNotes || []) m.set(n.id, n);
    return m;
  }, [availableNotes]);
  const allNoteLinks = (note.noteLinks || [])
    .map((l) => ({ ...l, target: notesById.get(l.targetNoteId) }))
    .filter((l) => l.target);
  const featuredNoteLinks = allNoteLinks.filter((l) => l.featured);
  const noteLinkCount = allNoteLinks.length;

  return (
    <Card
      className="bg-[#121212] border-white/5 transition-colors hover:border-[#e11d74]/40 overflow-hidden"
      data-testid={`character-card-${note.id}`}
      style={{ boxShadow: showMedium ? `0 0 0 1px ${MAGENTA}30` : undefined }}
    >
      <CardContent className={tier === 0 ? "p-2.5" : "p-3"}>
        <div className="flex items-start gap-2">
          <UserRound className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: MAGENTA }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={cycleTier}
                className="flex items-center gap-1 text-white/90 font-medium text-sm hover:text-pink-300 transition-colors text-left"
                title={tier === 0 ? "Click to show portrait" : tier === 1 ? "Click to expand all details" : "Click to collapse"}
                data-testid={`character-title-${note.id}`}
              >
                <ChevronRight
                  className="w-3.5 h-3.5 transition-transform shrink-0"
                  style={{ transform: `rotate(${tier * 45}deg)` }}
                />
                {note.title || "Unnamed Character"}
              </button>
              {statCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/15 text-pink-300 border border-pink-400/20">
                  {statCount} stat{statCount === 1 ? "" : "s"}
                </span>
              )}
              {imgCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">
                  {imgCount} image{imgCount === 1 ? "" : "s"}
                </span>
              )}
              {noteLinkCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-400/20">
                  {noteLinkCount} link{noteLinkCount === 1 ? "" : "s"}
                </span>
              )}
              <button
                onClick={() => onEdit(note)}
                className="ml-auto text-white/50 hover:text-pink-400 transition-colors shrink-0"
                title="Edit character"
                data-testid={`edit-character-${note.id}`}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              {headerExtras}
              {onDelete && (
                <button
                  onClick={() => {
                    if (window.confirm(`Delete character "${note.title || "Unnamed"}"? This can't be undone.`)) {
                      onDelete(note.id);
                    }
                  }}
                  className="text-white/50 hover:text-red-400 transition-colors shrink-0"
                  title="Delete character"
                  data-testid={`delete-character-${note.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Primary image (120x120, aspect-preserved) sits to the left of
                the achievements strip so unlocks flow naturally beside the
                character portrait. Additional images stay in the expanded
                bottom grid. When the user stars a stat, it renders above the
                strip so a key value (e.g. HP: 9999) is always visible on the
                collapsed card. The right column is `h-[120px]` and stacked
                `justify-end` so the achievement strip bottom always aligns
                with the bottom edge of the portrait, regardless of whether
                a featured stat is shown.
                Rendered only in tier ≥ 1 so the Minimal tier stays as
                compact as a plain-text note. */}
            {showMedium && (
            <div className="mt-2 flex items-start gap-3">
              {primaryImage && (
                <a
                  href={primaryImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`shrink-0 rounded overflow-hidden bg-black/60 border border-white/10 hover:border-pink-400/40 transition-colors flex items-center justify-center ${
                    showFull ? "w-[120px] h-[120px]" : "w-[60px] h-[60px]"
                  }`}
                  title="Open image in new tab"
                  data-testid={`character-primary-image-${note.id}`}
                >
                  <img
                    src={primaryImage}
                    alt=""
                    loading="lazy"
                    className="max-w-full max-h-full object-contain"
                  />
                </a>
              )}
              <div
                className={`flex-1 min-w-0 flex flex-col justify-end ${
                  primaryImage ? (showFull ? "min-h-[120px]" : "min-h-[60px]") : ""
                }`}
              >
                {featuredNoteLinks.length > 0 && (
                  <div
                    className="mb-1.5 flex flex-wrap items-center gap-1.5"
                    data-testid={`character-featured-notelinks-${note.id}`}
                  >
                    {featuredNoteLinks.map((l) => (
                      <NoteLinkPill key={l.id} link={l} />
                    ))}
                  </div>
                )}
                {featuredStats.length > 0 && (
                  <div
                    className="mb-1 text-xs cn-featured-stats-container"
                    style={{ containerType: "inline-size" }}
                    data-testid={`character-featured-stats-${note.id}`}
                  >
                    <style>{`
                      /* Featured stats are always shown one per line inside the
                         card so long strings (e.g. "Current Alignment: Villain")
                         wrap cleanly instead of getting truncated or shoved
                         out of the container. Each stat becomes name + value
                         side-by-side; on very narrow cards they stack. */
                      .cn-featured-stats-container {
                        display: flex;
                        flex-direction: column;
                        row-gap: 0.125rem;
                      }
                      .cn-featured-stat-row {
                        display: flex;
                        align-items: baseline;
                        flex-wrap: wrap;
                        gap: 0 0.375rem;
                        min-width: 0;
                      }
                      .cn-featured-stat-row .cn-stat-label,
                      .cn-featured-stat-row .cn-stat-value {
                        overflow-wrap: anywhere;
                        word-break: normal;
                        min-width: 0;
                      }
                      /* When card is very narrow, force name onto its own line
                         above the value so both stay readable. */
                      @container (max-width: 220px) {
                        .cn-featured-stat-row {
                          flex-direction: column;
                          align-items: flex-start;
                          gap: 0;
                        }
                      }
                    `}</style>
                    {featuredStats.map((s, idx) => (
                      <div
                        key={s.id || `${s.name}-${idx}`}
                        className="cn-featured-stat-row"
                        data-testid={`character-featured-stat-${note.id}-${idx}`}
                      >
                        <span className="flex items-baseline gap-1 shrink-0">
                          <Star className="w-3 h-3 fill-amber-300 text-amber-300 shrink-0 self-center" />
                          <span className="cn-stat-label text-pink-300/80 font-medium">{s.name}</span>
                        </span>
                        <span className="cn-stat-value text-white/70 tabular-nums">{s.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <CharacterAchievementsStrip
                  characterId={note.id}
                  achievements={achievements}
                  pageSize={showFull ? 10 : 6}
                />
              </div>
            </div>
            )}

            {showFull && (
              <div className="mt-3 space-y-3">
                {(() => {
                  // In the full view, hide stats that are already shown in
                  // the medium tier's featured strip so we don't repeat them.
                  // Non-featured stats keep their spot in the grid below.
                  const nonFeatured = (note.statistics || []).filter(s => !s?.featured);
                  if (nonFeatured.length === 0) return null;
                  return (
                    <div
                      className="cn-nonfeatured-grid"
                      style={{ containerType: "inline-size" }}
                      data-testid={`character-statistics-${note.id}`}
                    >
                      <style>{`
                        /* Non-featured stat chips always adapt to the CARD
                           width, not the viewport. On narrow cards (e.g. the
                           4-column User Notes layout) we stack them; on wider
                           cards we allow two per row. */
                        .cn-nonfeatured-grid {
                          display: grid;
                          grid-template-columns: 1fr;
                          gap: 0.375rem;
                        }
                        @container (min-width: 360px) {
                          .cn-nonfeatured-grid {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                          }
                        }
                      `}</style>
                      {nonFeatured.map((s) => (
                        <div
                          key={s.id}
                          className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 px-2 py-1 rounded bg-black/40 border border-white/5 text-xs min-w-0"
                        >
                          <span
                            className="text-pink-300/80 font-medium min-w-0"
                            style={{ overflowWrap: "anywhere" }}
                          >
                            {s.name}
                          </span>
                          <span
                            className="text-white/70 tabular-nums text-right min-w-0"
                            style={{ overflowWrap: "anywhere" }}
                          >
                            {s.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {(() => {
                  const nonFeatured = allNoteLinks.filter((l) => !l.featured);
                  if (nonFeatured.length === 0) return null;
                  return (
                    <div className="flex flex-wrap items-center gap-1.5" data-testid={`character-notelinks-${note.id}`}>
                      {nonFeatured.map((l) => (
                        <NoteLinkPill key={l.id} link={l} />
                      ))}
                    </div>
                  );
                })()}

                {note.text && (
                  <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed" data-testid={`character-notes-${note.id}`}>
                    {note.text}
                  </div>
                )}

                {additionalImages.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid={`character-images-${note.id}`}>
                    {additionalImages.map((url, i) => (
                      <a
                        key={`${url}-${i}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square bg-black/40 rounded overflow-hidden border border-white/5 hover:border-pink-400/40 transition-colors"
                        title="Open image in new tab"
                      >
                        <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Modal for creating and editing a Character note.
// `masterGameId` (optional) enables the per-game template picker.
export function CharacterEditor({ open, onOpenChange, initial, onSave, onCancel, masterGameId, availableNotes = [] }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [statistics, setStatistics] = useState([]);
  const [images, setImages] = useState([]);
  const [noteLinks, setNoteLinks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Reset on open
  const [lastOpen, setLastOpen] = useState(false);
  if (open && !lastOpen) {
    setName(initial?.title || "");
    setText(initial?.text || "");
    setStatistics(
      (initial?.statistics || []).map((s) => ({
        id: s.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: s.name || "",
        value: s.value || "",
        featured: !!s.featured,
      }))
    );
    setImages([...(initial?.images || [])]);
    setNoteLinks(
      (initial?.noteLinks || []).map((l) => ({
        id: l.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        targetNoteId: l.targetNoteId || "",
        displayMode: l.displayMode || "title",
        customText: l.customText || "",
        iconKey: l.iconKey || "link",
        featured: !!l.featured,
      }))
    );
    setLastOpen(true);
  }
  if (!open && lastOpen) setLastOpen(false);

  const addStat = () =>
    setStatistics([
      ...statistics,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: "", value: "", featured: false },
    ]);
  const updateStat = (id, field, val) =>
    setStatistics(statistics.map((s) => (s.id === id ? { ...s, [field]: val } : s)));
  // Up to MAX_FEATURED stats can be starred. Clicking a starred stat
  // unstars it. Clicking an unstarred stat while the cap is already hit is
  // a no-op — the button visually dims so the user knows why.
  const MAX_FEATURED = 4;
  const featuredCount = statistics.filter((s) => s.featured).length;
  const toggleStatFeatured = (id) =>
    setStatistics((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (s.featured) return { ...s, featured: false };
        if (prev.filter((x) => x.featured).length >= MAX_FEATURED) return s;
        return { ...s, featured: true };
      })
    );
  const removeStat = (id) => setStatistics(statistics.filter((s) => s.id !== id));

  // ============ NOTE LINKS ============
  // Same feature/star ceiling as stats (max 4 featured; max 20 total).
  const noteLinkFeaturedCount = noteLinks.filter((l) => l.featured).length;
  const addNoteLink = () => {
    if (noteLinks.length >= NOTELINK_MAX) return;
    setNoteLinks([
      ...noteLinks,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        targetNoteId: "",
        displayMode: "title",
        customText: "",
        iconKey: "link",
        featured: false,
      },
    ]);
  };
  const updateNoteLink = (id, patch) =>
    setNoteLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const toggleNoteLinkFeatured = (id) =>
    setNoteLinks((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        if (l.featured) return { ...l, featured: false };
        if (prev.filter((x) => x.featured).length >= NOTELINK_MAX_FEATURED) return l;
        return { ...l, featured: true };
      })
    );
  const removeNoteLink = (id) => setNoteLinks(noteLinks.filter((l) => l.id !== id));
  const [nlDragIndex, setNlDragIndex] = useState(null);
  const nlOnDrop = (idx) => {
    if (nlDragIndex === null || nlDragIndex === idx) return;
    setNoteLinks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(nlDragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setNlDragIndex(null);
  };

  // Drag-and-drop reorder using HTML5 drag events. Row index is stored in
  // `dragIndex`; on drop we splice the moving row into the new position.
  const [dragIndex, setDragIndex] = useState(null);
  const onDragStart = (idx) => setDragIndex(idx);
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) return;
    setStatistics((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const addImageSlot = () => {
    if (images.length >= MAX_IMAGES) return;
    setImages([...images, ""]);
  };
  const updateImage = (i, val) => {
    const next = [...images];
    next[i] = val;
    setImages(next);
  };
  const removeImage = (i) => setImages(images.filter((_, idx) => idx !== i));

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      title: name.trim(),
      text: text.trim(),
      statistics: statistics
        .filter((s) => s.name.trim() || s.value.trim())
        .map((s) => ({ id: s.id, name: s.name.trim(), value: s.value.trim(), featured: !!s.featured })),
      images: images.map((u) => (u || "").trim()).filter(Boolean),
      noteLinks: noteLinks
        .filter((l) => l.targetNoteId)
        .map((l) => ({
          id: l.id,
          targetNoteId: l.targetNoteId,
          displayMode: l.displayMode,
          customText: l.customText || "",
          iconKey: l.iconKey || "link",
          featured: !!l.featured,
        })),
    });
  };

  // Template loading. Keyed on masterGameId — user's own templates come
  // first, then any published community templates for the same game.
  const API = `${getApiUrl()}/api`;
  const loadTemplates = useCallback(async () => {
    if (!masterGameId) return;
    try {
      const res = await axios.get(`${API}/character-templates`, { params: { masterGameId } });
      setTemplates(res.data?.templates || []);
    } catch { /* silent — templates are optional */ }
  }, [masterGameId, API]);

  useEffect(() => { if (open) loadTemplates(); }, [open, loadTemplates]);

  const applyTemplate = (t) => {
    // Overwrite the stat rows with the template's structure. Values start
    // empty so the user fills them in fresh for this character.
    setStatistics(t.statistics.map((s, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
      name: s.name,
      value: "",
      featured: !!s.featured,
    })));
    toast.success(`Applied template "${t.name}"`);
  };

  const saveAsTemplate = async () => {
    if (!masterGameId) { toast.error("No master game linked to save a template for"); return; }
    const cleaned = statistics.filter((s) => s.name.trim());
    if (cleaned.length === 0) { toast.error("Add at least one stat first"); return; }
    const finalName = (templateName || name || "Untitled").trim();
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/character-templates`, {
        masterGameId,
        name: finalName,
        statistics: cleaned.map((s) => ({ name: s.name.trim(), featured: !!s.featured })),
      });
      toast.success(`Template "${finalName}" saved`);
      setTemplateName("");
      await loadTemplates();
    } catch (e) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const togglePublish = async (t) => {
    try {
      const path = t.published ? "unpublish" : "publish";
      await axios.post(`${API}/character-templates/${t.id}/${path}`);
      toast.success(t.published ? "Unpublished" : "Published to community");
      await loadTemplates();
    } catch { toast.error("Failed"); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/character-templates/${t.id}`);
      toast.success("Template deleted");
      await loadTemplates();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="character-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound className="w-5 h-5" style={{ color: MAGENTA }} />
            {initial ? "Edit Character" : "Add Character"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-white/70 text-sm">Character Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Cloud Strife"
              className="bg-black/50 border-white/10"
              data-testid="character-name-input"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-white/70 text-sm">
                Statistics
                <span className="ml-2 text-[10px] text-white/40 font-normal">
                  (name + value; add as many as you need)
                </span>
              </Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={addStat}
                className="h-7 text-[10px] text-pink-300 hover:text-pink-200 hover:bg-pink-500/10"
                data-testid="character-add-stat-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add stat
              </Button>
            </div>
            {statistics.length === 0 && (
              <p className="text-[11px] text-white/30 italic">No statistics yet.</p>
            )}
            {statistics.length > 0 && (
              <p className="text-[10px] text-white/40">
                Star up to <span className="text-amber-300">{MAX_FEATURED}</span> stats to feature them on the collapsed card
                ({featuredCount}/{MAX_FEATURED} used). Drag rows to reorder.
              </p>
            )}

            {/* Templates picker + save-as-template. Only visible when the
                editor knows its master game (i.e., opened from a game). */}
            {masterGameId && (
              <div className="rounded border border-white/10 bg-black/30 p-2 space-y-2" data-testid="character-templates-panel">
                <div className="flex items-center gap-2 text-xs text-white/70">
                  <LayoutTemplate className="w-3.5 h-3.5 text-pink-300" />
                  <span className="font-medium">Templates</span>
                  <span className="text-[10px] text-white/40">stat structure only · values start empty</span>
                </div>
                {templates.length === 0 ? (
                  <p className="text-[11px] text-white/40 italic">No templates for this game yet.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-xs p-1 rounded hover:bg-white/5" data-testid={`character-template-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-white/85">{t.name}</div>
                          <div className="text-[10px] text-white/40 truncate">
                            {t.mine ? "By you" : `By ${t.userName || "another player"}`}
                            {" · "}
                            {t.statistics.length} stat{t.statistics.length === 1 ? "" : "s"}
                            {t.published && <span className="text-emerald-300"> · Published</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => applyTemplate(t)} data-testid={`template-apply-${t.id}`}>
                          Apply
                        </Button>
                        {t.mine && (
                          <>
                            <button
                              onClick={() => togglePublish(t)}
                              className={`shrink-0 ${t.published ? "text-emerald-300" : "text-white/40 hover:text-emerald-300"}`}
                              title={t.published ? "Unpublish from community" : "Publish to community"}
                              data-testid={`template-publish-${t.id}`}
                            >
                              {t.published ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => deleteTemplate(t)}
                              className="shrink-0 text-white/30 hover:text-red-400"
                              title="Delete template"
                              data-testid={`template-delete-${t.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Save current stats as new template…"
                    className="bg-black/50 border-white/10 flex-1 h-8 text-xs"
                    data-testid="template-name-input"
                  />
                  <Button
                    size="sm"
                    onClick={saveAsTemplate}
                    disabled={savingTemplate || statistics.filter((s) => s.name.trim()).length === 0}
                    data-testid="template-save-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            )}
            {statistics.map((s, idx) => {
              const capReached = !s.featured && featuredCount >= MAX_FEATURED;
              return (
              <div
                key={s.id}
                className={`flex items-center gap-2 ${dragIndex === idx ? "opacity-50" : ""}`}
                data-testid={`character-stat-row-${idx}`}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(idx)}
                onDragEnd={() => setDragIndex(null)}
              >
                <span
                  className="shrink-0 text-white/25 cursor-grab active:cursor-grabbing hover:text-white/50 select-none"
                  title="Drag to reorder"
                  data-testid={`character-stat-drag-${idx}`}
                >
                  ⋮⋮
                </span>
                <button
                  type="button"
                  onClick={() => toggleStatFeatured(s.id)}
                  disabled={capReached}
                  className={`shrink-0 transition-colors ${
                    s.featured
                      ? "text-amber-300"
                      : capReached
                        ? "text-white/10 cursor-not-allowed"
                        : "text-white/25 hover:text-amber-300"
                  }`}
                  title={
                    s.featured
                      ? "Featured — click to unfeature"
                      : capReached
                        ? `Only ${MAX_FEATURED} stats can be featured — unstar another first`
                        : "Feature this stat on the collapsed card"
                  }
                  data-testid={`character-stat-feature-${idx}`}
                >
                  <Star className={`w-4 h-4 ${s.featured ? "fill-amber-300" : ""}`} />
                </button>
                <Input
                  value={s.name}
                  onChange={(e) => updateStat(s.id, "name", e.target.value)}
                  placeholder="Stat name (e.g., HP)"
                  className="bg-black/50 border-white/10 flex-1"
                />
                <Input
                  value={s.value}
                  onChange={(e) => updateStat(s.id, "value", e.target.value)}
                  placeholder="Value (e.g., 9999)"
                  className="bg-black/50 border-white/10 flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeStat(s.id)}
                  className="text-white/30 hover:text-red-400 shrink-0"
                  title="Remove stat"
                  data-testid={`character-remove-stat-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              );
            })}
          </div>

          <div className="space-y-1">
            <Label className="text-white/70 text-sm">Notes</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Any lore, build ideas, achievement tips…"
              rows={5}
              className="bg-black/50 border-white/10 resize-y"
              data-testid="character-notes-input"
            />
          </div>

          {/* NOTE LINKS ------------------------------------------------ */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-white/70 text-sm">
                Note Links
                <span className="ml-2 text-[10px] text-white/40 font-normal">
                  ({noteLinks.length}/{NOTELINK_MAX}) — link to plain notes on this game
                </span>
              </Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={addNoteLink}
                disabled={noteLinks.length >= NOTELINK_MAX}
                className="h-7 text-[10px] text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10"
                data-testid="character-add-notelink-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Add link
              </Button>
            </div>
            {availableNotes.length === 0 ? (
              <p className="text-[11px] text-white/30 italic">
                No plain notes on this game yet — add one first to link it here.
              </p>
            ) : noteLinks.length === 0 ? (
              <p className="text-[11px] text-white/30 italic">No links yet.</p>
            ) : (
              <p className="text-[10px] text-white/40">
                Star up to <span className="text-amber-300">{NOTELINK_MAX_FEATURED}</span> links to feature them on the collapsed card
                ({noteLinkFeaturedCount}/{NOTELINK_MAX_FEATURED} used). Drag rows to reorder.
              </p>
            )}
            {noteLinks.map((l, idx) => {
              const capReached = !l.featured && noteLinkFeaturedCount >= NOTELINK_MAX_FEATURED;
              const target = availableNotes.find((n) => n.id === l.targetNoteId);
              return (
                <div
                  key={l.id}
                  className={`rounded border border-white/10 bg-black/30 p-2 space-y-2 ${nlDragIndex === idx ? "opacity-50" : ""}`}
                  data-testid={`character-notelink-row-${idx}`}
                  draggable
                  onDragStart={() => setNlDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => nlOnDrop(idx)}
                  onDragEnd={() => setNlDragIndex(null)}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="shrink-0 text-white/25 cursor-grab active:cursor-grabbing hover:text-white/50 select-none"
                      title="Drag to reorder"
                      data-testid={`character-notelink-drag-${idx}`}
                    >
                      ⋮⋮
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleNoteLinkFeatured(l.id)}
                      disabled={capReached}
                      className={`shrink-0 transition-colors ${
                        l.featured
                          ? "text-amber-300"
                          : capReached
                            ? "text-white/10 cursor-not-allowed"
                            : "text-white/25 hover:text-amber-300"
                      }`}
                      title={
                        l.featured
                          ? "Featured — click to unfeature"
                          : capReached
                            ? `Only ${NOTELINK_MAX_FEATURED} links can be featured — unstar another first`
                            : "Feature this link on the collapsed card"
                      }
                      data-testid={`character-notelink-feature-${idx}`}
                    >
                      <Star className={`w-4 h-4 ${l.featured ? "fill-amber-300" : ""}`} />
                    </button>
                    <Select
                      value={l.targetNoteId || undefined}
                      onValueChange={(v) => updateNoteLink(l.id, { targetNoteId: v })}
                    >
                      <SelectTrigger
                        className="bg-black/50 border-white/10 flex-1 h-9 text-xs"
                        data-testid={`character-notelink-target-${idx}`}
                      >
                        <SelectValue placeholder="Pick a note to link…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0d0d0d] border-white/10 text-white">
                        {availableNotes.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            <span className="inline-flex items-center gap-1.5">
                              <StickyNote className="w-3 h-3 text-cyan-300/70" />
                              <span className="truncate max-w-[240px]">{n.title || (n.text || "Untitled").slice(0, 40)}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={l.displayMode}
                      onValueChange={(v) => updateNoteLink(l.id, { displayMode: v })}
                    >
                      <SelectTrigger
                        className="bg-black/50 border-white/10 h-9 text-xs w-[110px]"
                        data-testid={`character-notelink-mode-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0d0d0d] border-white/10 text-white">
                        <SelectItem value="title">Title</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="text">Custom text</SelectItem>
                        <SelectItem value="icon">Icon</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => removeNoteLink(l.id)}
                      className="text-white/30 hover:text-red-400 shrink-0"
                      title="Remove link"
                      data-testid={`character-notelink-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {l.displayMode === "text" && (
                    <Input
                      value={l.customText}
                      onChange={(e) => updateNoteLink(l.id, { customText: e.target.value })}
                      placeholder="Custom label (e.g., Build guide)"
                      className="bg-black/50 border-white/10 h-8 text-xs"
                      data-testid={`character-notelink-custom-${idx}`}
                    />
                  )}
                  {(l.displayMode === "icon" || (l.displayMode === "image" && target && !target.imageUrl)) && (
                    <div className="flex flex-wrap items-center gap-1">
                      {l.displayMode === "image" && (
                        <span className="text-[10px] text-white/40 mr-1 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> No image on target — pick a fallback icon:
                        </span>
                      )}
                      {NOTELINK_ICON_ORDER.map((key) => {
                        const IconCmp = NOTELINK_ICONS[key];
                        const selected = l.iconKey === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => updateNoteLink(l.id, { iconKey: key })}
                            className={`w-7 h-7 rounded border flex items-center justify-center transition-colors ${
                              selected
                                ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200"
                                : "bg-black/40 border-white/10 text-white/50 hover:text-white hover:border-white/25"
                            }`}
                            title={key}
                            data-testid={`character-notelink-icon-${idx}-${key}`}
                          >
                            <IconCmp className="w-3.5 h-3.5" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-white/70 text-sm">
                Images
                <span className="ml-2 text-[10px] text-white/40 font-normal">
                  ({images.length}/{MAX_IMAGES}) — paste direct image URLs (user-hosted)
                </span>
              </Label>
              {images.length < MAX_IMAGES && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addImageSlot}
                  className="h-7 text-[10px] text-pink-300 hover:text-pink-200 hover:bg-pink-500/10"
                  data-testid="character-add-image-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add image
                </Button>
              )}
            </div>
            {images.map((url, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`character-image-row-${i}`}>
                <div className="w-10 h-10 rounded bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                  {url ? (
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <UserRound className="w-4 h-4 text-white/20" />
                  )}
                </div>
                <Input
                  value={url}
                  onChange={(e) => updateImage(i, e.target.value)}
                  placeholder="https://…"
                  className="bg-black/50 border-white/10 flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="text-white/30 hover:text-red-400 shrink-0"
                  title="Remove image"
                  data-testid={`character-remove-image-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} className="text-white/60 hover:text-white">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim()}
            style={{ background: MAGENTA }}
            className="hover:brightness-110 text-white"
            data-testid="character-save-btn"
          >
            {initial ? "Save Changes" : "Add Character"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
